/**
 * Contract Package Import — human-readable field layouts for the map step.
 *
 * The bespoke Project / Contract workspaces render their Overview as labeled sections (Identity, Schedule,
 * Financials, …) with friendly field labels instead of raw column names. The importer's map step should
 * read the same way, so this mirrors those section+label layouts (Project/Contract transcribed from
 * ProjectWorkspace-Provider / ContractWorkspace-Provider; Bid Item / Line Item authored here since they
 * have no bespoke workspace). Any settable field not named in a layout falls into an "Additional fields"
 * section with a humanized label — so nothing is lost, it's just deprioritized.
 *
 * Pure data + helpers — no pict / REST. Each layout is an ordered list of { Section, Fields:[{Key,Label}] }.
 * (Audit + the GUID/PK/strategy-owned FK columns are intentionally omitted — they aren't mapped from data.)
 */

'use strict';

const LAYOUTS =
{
	Contract:
	[
		{ Section: 'Identity', Fields: [ { Key: 'Name', Label: 'Contract Name' }, { Key: 'Status', Label: 'Status' }, { Key: 'ExternalContractID', Label: 'External Contract ID' } ] },
		{ Section: 'Project Numbers', Fields: [ { Key: 'PrimaryProjectNumber', Label: 'Primary' }, { Key: 'CountyProjectNumber', Label: 'County' }, { Key: 'StateProjectNumber', Label: 'State' }, { Key: 'FederalProjectNumber', Label: 'Federal' } ] },
		{ Section: 'Route & Limits', Fields: [ { Key: 'RouteNumber', Label: 'Route #' }, { Key: 'HighwayNumber', Label: 'Highway #' }, { Key: 'PavingMaterial', Label: 'Paving Material' }, { Key: 'StationBegin', Label: 'Station Begin' }, { Key: 'TerminusBegin', Label: 'Terminus Begin' }, { Key: 'StationEnd', Label: 'Station End' }, { Key: 'TerminusEnd', Label: 'Terminus End' } ] },
		{ Section: 'Financials', Fields: [ { Key: 'TotalBidAmount', Label: 'Total Bid Amount' }, { Key: 'NetChangeOrderAmount', Label: 'Net Change Orders' }, { Key: 'VendorAccountCode', Label: 'Vendor Account Code' } ] },
		{ Section: 'Specifications', Fields: [ { Key: 'ItemSpecificationYear', Label: 'Item Spec Year' }, { Key: 'MaterialsSpecificationYear', Label: 'Materials Spec Year' }, { Key: 'ItemIDTestSpecificationSet', Label: 'Item Test Spec Set' }, { Key: 'MaterialIDTestSpecificationSet', Label: 'Material Test Spec Set' }, { Key: 'DBEStructure', Label: 'DBE Structure' } ] },
		{ Section: 'Relationships & Schedule', Fields: [ { Key: 'IDOrganization', Label: 'Prime Contractor' }, { Key: 'IDContractType', Label: 'Contract Type' }, { Key: 'IDCustomer', Label: 'Customer' }, { Key: 'EstimatedCompletionDate', Label: 'Estimated Completion' } ] },
	],
	Project:
	[
		{ Section: 'Identity', Fields: [ { Key: 'Name', Label: 'Project Name' }, { Key: 'ExternalProjectID', Label: 'External Project ID' }, { Key: 'Status', Label: 'Status' }, { Key: 'ImprovementTypeCode', Label: 'Improvement Type' } ] },
		{ Section: 'Schedule', Fields: [ { Key: 'OriginalContractedDays', Label: 'Original Contracted Days' }, { Key: 'ContractedDays', Label: 'Contracted Days' }, { Key: 'CompletedDays', Label: 'Completed Days' }, { Key: 'PaidDays', Label: 'Paid Days' }, { Key: 'PercentComplete', Label: 'Percent Complete' }, { Key: 'PercentCompleteByDays', Label: 'Percent Complete (by days)' }, { Key: 'Timezone', Label: 'Timezone' } ] },
		{ Section: 'Financials', Fields: [ { Key: 'CostPerDay', Label: 'Cost / Day' }, { Key: 'InitialContractAmount', Label: 'Initial Contract' }, { Key: 'CurrentTotalContractAmount', Label: 'Current Total Contract' }, { Key: 'CurrentTotalContractPaid', Label: 'Total Paid' }, { Key: 'PurchaseOrderNumber', Label: 'Purchase Order #' } ] },
		{ Section: 'Relationships', Fields: [ { Key: 'IDOrganization', Label: 'Prime Contractor' }, { Key: 'IDProjectType', Label: 'Project Type' }, { Key: 'IDCustomer', Label: 'Customer' } ] },
	],
	BidItem:
	[
		{ Section: 'Identity', Fields: [ { Key: 'Name', Label: 'Name' }, { Key: 'Description', Label: 'Description' } ] },
		{ Section: 'Quantities', Fields: [ { Key: 'Quantity', Label: 'Quantity' }, { Key: 'Units', Label: 'Units' }, { Key: 'CostPerUnit', Label: 'Cost / Unit' } ] },
		{ Section: 'Relationships', Fields: [ { Key: 'IDPayItem', Label: 'Pay Item' }, { Key: 'IDOrganization', Label: 'Prime Contractor' }, { Key: 'IDCustomer', Label: 'Customer' } ] },
	],
	LineItem:
	[
		{ Section: 'Identity', Fields: [ { Key: 'Name', Label: 'Name' }, { Key: 'Description', Label: 'Description' }, { Key: 'ItemCode', Label: 'Item Code' }, { Key: 'ItemNumber', Label: 'Item Number' }, { Key: 'Status', Label: 'Status' }, { Key: 'ImprovementType', Label: 'Improvement Type' }, { Key: 'CategoryNumber', Label: 'Category #' }, { Key: 'ProposalIdentifier', Label: 'Proposal ID' }, { Key: 'ChangeOrderNumber', Label: 'Change Order #' } ] },
		{ Section: 'Quantities', Fields: [ { Key: 'Units', Label: 'Units' }, { Key: 'ExpectedQuantity', Label: 'Expected Qty' }, { Key: 'ContractQuantity', Label: 'Contract Qty' }, { Key: 'ActualQuantity', Label: 'Actual Qty' }, { Key: 'InstalledQuantity', Label: 'Installed Qty' }, { Key: 'ContractorReportedQuantity', Label: 'Contractor Reported Qty' }, { Key: 'ChangeOrderQuantity', Label: 'Change Order Qty' }, { Key: 'ChangeOrderQuantityPending', Label: 'CO Qty (pending)' } ] },
		{ Section: 'Financials', Fields: [ { Key: 'CostPerUnit', Label: 'Cost / Unit' }, { Key: 'Tax', Label: 'Tax' }, { Key: 'Retainage', Label: 'Retainage' }, { Key: 'PercentComplete', Label: 'Percent Complete' }, { Key: 'FHWAEligible', Label: 'FHWA Eligible' } ] },
		{ Section: 'Schedule & Specs', Fields: [ { Key: 'DeliveryDate', Label: 'Delivery Date' }, { Key: 'SpecificationYear', Label: 'Spec Year' } ] },
		{ Section: 'Relationships', Fields: [ { Key: 'IDPayItem', Label: 'Pay Item' }, { Key: 'IDFundingCategory', Label: 'Funding Category' }, { Key: 'FundingCategory', Label: 'Funding Category (text)' }, { Key: 'IDCustomer', Label: 'Customer' } ] },
	],
};

/**
 * Humanize a raw column name for the fallback "Additional fields" section:
 * strip a leading `ID`, split camelCase, tidy runs of capitals. e.g. `PurchaseOrderNumber` -> `Purchase
 * Order Number`, `IDPayItem` -> `Pay Item`, `FHWAEligible` -> `FHWA Eligible`.
 * @param {string} pName @returns {string}
 */
function humanize(pName)
{
	let tmpName = String(pName || '');
	if (/^ID[A-Z]/.test(tmpName)) { tmpName = tmpName.slice(2); }
	return tmpName
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim();
}

/**
 * Group a set of settable field names into ordered { Section, Fields:[{Key,Label}] } per the entity layout;
 * anything not named in the layout lands in a trailing "Additional fields" section with a humanized label.
 * @param {string} pEntity @param {Array<string>} pFieldNames
 * @returns {Array<{Section:string, Fields:Array<{Key:string, Label:string}>}>}
 */
function groupFields(pEntity, pFieldNames)
{
	const tmpAvailable = {};
	(pFieldNames || []).forEach((pName) => { tmpAvailable[pName] = true; });
	const tmpUsed = {};
	const tmpGroups = [];

	(LAYOUTS[pEntity] || []).forEach((pSection) =>
	{
		const tmpFields = pSection.Fields
			.filter((pField) => tmpAvailable[pField.Key])
			.map((pField) => { tmpUsed[pField.Key] = true; return { Key: pField.Key, Label: pField.Label }; });
		if (tmpFields.length > 0) { tmpGroups.push({ Section: pSection.Section, Fields: tmpFields }); }
	});

	const tmpExtra = (pFieldNames || [])
		.filter((pName) => !tmpUsed[pName])
		.map((pName) => ({ Key: pName, Label: humanize(pName) }));
	if (tmpExtra.length > 0) { tmpGroups.push({ Section: 'Additional fields', Fields: tmpExtra }); }

	return tmpGroups;
}

/**
 * Resolve a source-column header to a field key for an entity — by exact/normalized field key OR by the
 * friendly Label. Lets a template's human headers ("Cost / Unit", "Purchase Order #") auto-map to the
 * schema fields (CostPerUnit, PurchaseOrderNumber). Returns '' when nothing matches.
 * @param {string} pEntity @param {string} pHeader @returns {string}
 */
function keyForHeader(pEntity, pHeader)
{
	const tmpNorm = (pStr) => String(pStr || '').toLowerCase().replace(/[^a-z0-9]/g, '');
	const tmpTarget = tmpNorm(pHeader);
	if (!tmpTarget) { return ''; }
	let tmpKey = '';
	(LAYOUTS[pEntity] || []).some((pSection) => pSection.Fields.some((pField) =>
	{
		if ((tmpNorm(pField.Label) === tmpTarget) || (tmpNorm(pField.Key) === tmpTarget)) { tmpKey = pField.Key; return true; }
		return false;
	}));
	return tmpKey;
}

module.exports = { LAYOUTS, humanize, groupFields, keyForHeader };
